import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FunctionComponent, useEffect, useState } from 'react';
import Header from './Header';
import TipsDetailTable from './TipsDetailTable';
import { getInvoicesTipDetailReport } from 'actions/tipsReport/TipsReportActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { TipsReportState, TipsReportStatus } from 'reducers/tipsReport/TipsReportState';
import { convertToStaffFilter, convertToTipDetails } from './TipDetailsConverter';
import Summary from './Summary';
import TipsDetailExportCSV from './TipsDetailExportCSV';
import { TipDetail, TipInvoice } from 'model/TipsReport';
import { Option, Period } from '@spike/model';
import moment from 'moment-timezone';
import { Pagination } from 'components/UI/V3/Pagination/Pagination';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            height: 'fit-content',
            padding: '24px 0px',
            [theme.breakpoints.up('md')]: {
                padding: '50px 24px'
            }
        },
        title: {
            color: '#222',
            fontSize: 20,
            lineHeight: 1,
            fontWeight: 600,
            [theme.breakpoints.down('sm')]: {
                padding: '0px 16px'
            },
            [theme.breakpoints.up('md')]: {
                fontSize: '24px'
            }
        },
        mobileWrapper: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24,
                padding: '24px 16px 0px',
                backgroundColor: '#FAFAFA'
            }
        }
    })
);

const mergeStaffOptions = (current: Array<Option<number>>, incoming: Array<Option<number>>): Array<Option<number>> => {
    const optionsById = new Map<number, Option<number>>();
    current.forEach(option => optionsById.set(option.id, option));
    incoming.forEach(option => optionsById.set(option.id, option));
    return Array.from(optionsById.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const extractStaffOptionsFromTips = (invoices: Array<TipInvoice>): Array<Option<number>> => {
    const optionsById = new Map<number, Option<number>>();

    invoices.forEach(invoice => {
        invoice.tips.forEach(tip => {
            if (!tip.staffId || optionsById.has(tip.staffId)) {
                return;
            }

            const name = (tip.staffName || '').replace(/\s+/g, ' ').trim() || `Staff ${tip.staffId}`;
            optionsById.set(tip.staffId, { id: tip.staffId, name, description: name });
        });
    });

    return Array.from(optionsById.values()).sort((a, b) => a.name.localeCompare(b.name));
};

export const TipsDetail: FunctionComponent<{ showTitle?: boolean }> = ({ showTitle = true }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const tipsLoading = useSelector<RootState, boolean>(state => state.tipsReport.loading);
    const tipStatus = useSelector<RootState, TipsReportStatus>(state => state.tipsReport.status);
    const tipsDetail = useSelector<RootState, TipsReportState>(state => state.tipsReport);

    const [tipDetailsConverted, setTipDetailsConverted] = useState<Array<TipDetail> | null>(null);

    const [period, setPeriod] = useState<Period>({
        from: moment().startOf('month'),
        to: moment().endOf('month')
    });

    const [staffsToFilter, setStaffsToFilter] = useState<Array<number>>([]);
    const [staffOptions, setStaffOptions] = useState<Array<Option<number>>>([]);

    const [page, setPage] = useState<number>(1);
    const [size, setSize] = useState<number>(10);

    const handlePageChange = (p: number) => {
        setPage(p);
    };

    const handleSizeChange = (s: number) => {
        setSize(s);
        setPage(1);
    };

    useEffect(() => {
        dispatch(
            getInvoicesTipDetailReport(
                period.from.format('YYYY/MM/DD'),
                period.to.format('YYYY/MM/DD'),
                convertToStaffFilter(staffsToFilter),
                page,
                size
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, staffsToFilter, page, size]);

    useEffect(() => {
        if (tipStatus !== TipsReportStatus.GettingSuccess || !tipsDetail) {
            return;
        }

        const extracted = extractStaffOptionsFromTips(tipsDetail.invoices);

        setStaffOptions(previous => {
            if (staffsToFilter.length === 0) {
                return page === 1 ? extracted : mergeStaffOptions(previous, extracted);
            }

            return previous.length === 0 ? extracted : previous;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipStatus, tipsDetail.invoices, page]);

    useEffect(() => {
        if (tipStatus === TipsReportStatus.GettingSuccess && tipsDetail) {
            setTipDetailsConverted(convertToTipDetails(tipsDetail, staffsToFilter));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipStatus]);

    const handleChangePeriod = (dateFilter: Period) => {
        setPeriod({ from: dateFilter.from, to: dateFilter.to });
        setPage(1);
    };

    const handleSelectStaff = (staffs: Array<number>) => {
        setStaffsToFilter(staffs);
        setPage(1);
    };

    return (
        <Box className={classes.container}>
            {showTitle && <Typography className={classes.title}>Tips Detail</Typography>}

            <Box className={classes.mobileWrapper}>
                <Header
                    period={period}
                    staffOptions={staffOptions}
                    selectedStaffIds={staffsToFilter}
                    onChangePeriod={handleChangePeriod}
                    onStaffChange={handleSelectStaff}
                    exportButton={
                        Number(tipsDetail.totalTipsCount ?? 0) > 0 && (
                            <TipsDetailExportCSV
                                period={period}
                                staffIds={staffsToFilter}
                            />
                        )
                    }
                />

                <Summary
                    loading={tipsLoading}
                    totalTips={tipsDetail.totalTips}
                    totalTipsCount={tipsDetail.totalTipsCount}
                    totalBookings={tipsDetail.totalBookings}
                />
            </Box>

            <TipsDetailTable
                loading={tipsLoading}
                tipsDetail={tipDetailsConverted}
            />

            {tipsDetail && tipsDetail.invoices.length > 0 && (
                <Pagination
                    page={tipsDetail.meta?.page ?? page}
                    totalPages={tipsDetail.meta?.totalPages ?? 1}
                    size={tipsDetail.meta?.perPage ?? size}
                    isLoading={tipsLoading}
                    onPageChange={handlePageChange}
                    onSizeChange={handleSizeChange}
                />
            )}
        </Box>
    );
};

export default TipsDetail;
